import Chips from '@Ravnur-Inc/core/components/chips/chips';
import { $Types } from '@Ravnur-Inc/core/typings/tsx';
import { Prop } from '@Ravnur-Inc/decorators';
import sortBy from '@Ravnur-Inc/nanoutils/sortBy';
import { ErrorObject } from '@vuelidate/core';
import { Options, Vue } from 'vue-class-component';

import Lookup from '@/components/lookup/lookup';
import LookupRepository from '@/repositories/lookup-repository';
import BaseRepository from '@Ravnur-Inc/http/base-repository';

import './chips-input.scss';

const CN = 'chips-input';

type Props<E extends Entity> = {
  items: E[];
  propName: keyof SubType<E, string>;
  repository: LookupRepository<E>;
  label?: string;
  placeholder?: string;
  creator?: boolean;
  persistent?: E[];
  errors?: ErrorObject[];
  queryParams?: Record<string, unknown>;
};

type Emits<E> = {
  onChange: (value: E[]) => void;
};

@Options({
  emits: ['change', 'update:items'],
})
export default class ChipsInput<E extends Entity> extends Vue {
  declare $props: $Types<Props<E>, Emits<E>, 'items'>;

  @Prop({ type: Array, required: true }) public readonly items!: E[];
  @Prop({ type: String, required: true }) public readonly propName!: keyof SubType<E, string>;
  @Prop({ type: Object, required: true }) public readonly repository!: LookupRepository<E>;
  @Prop({ type: Object, required: true }) public createRepository?: BaseRepository<E>;
  @Prop({ type: String, default: '' }) public readonly label!: string;
  @Prop({ type: String, default: '' }) public readonly placeholder!: string;
  @Prop({ type: Boolean, default: true }) public readonly creator!: boolean;
  @Prop({ type: Boolean, default: false }) public readonly requiredAsterisk?: boolean;
  @Prop({ type: Array, default: () => [] }) public readonly persistent!: E[];
  @Prop({ type: Array, default: [] }) public readonly errors!: ErrorObject[];
  @Prop({ type: Object, default: () => ({}) })
  public readonly queryParams!: Record<string, unknown>;

  private highlighted: E[] = [];

  render() {
    return (
      <div class={CN}>
        <Lookup
          class={`${CN}__field`}
          createRepository={this.createRepository}
          creator={this.creator}
          errors={this.errors}
          label={this.label}
          placeholder={this.placeholder}
          propName={this.propName}
          queryParams={this.queryParams}
          repository={this.repository}
          requiredAsterisk={this.requiredAsterisk}
          v-slots={this.$slots}
          value={null}
          onSelect={this.add}
        />

        {/* key prop should be there bc we have issues in scrollable when it's empty and then you add an item */}
        <scrollable key={this.items.length} class={`${CN}__scrollable`}>
          <Chips
            class={`${CN}__items`}
            highlighted={this.highlighted}
            items={sortBy(this.comparator, this.items)}
            persistent={this.persistent}
            propName={this.propName}
            onChange={this.change}
          />
        </scrollable>
      </div>
    );
  }

  private add(entity: E) {
    const { items } = this;
    if (items.some((e) => e.id === entity.id)) {
      this.highlight(entity);
      return;
    }
    this.change(items.concat([entity]));
  }

  private change(items: E[]) {
    this.$emit('change', items);
    this.$emit('update:items', items);
  }

  private highlight(entity: E) {
    this.highlighted.push(entity);
    setTimeout(() => {
      this.highlighted = this.highlighted.filter((e) => e.id !== entity.id);
    }, 1000);
  }

  private comparator(e: E): string {
    const val = e[this.propName];
    return typeof val === 'string' ? val : '';
  }
}
